import { useNavigate } from 'react-router-dom';
import { Hand } from 'lucide-react';
import { relativeTime } from '@homework/shared';
import { useRealtime } from '../lib/realtime';
import { Avatar } from './ui';

/**
 * FR-6.2 — the non-intrusive prompt a person in Focus Mode sees when someone
 * wants to talk. Nothing connects until they choose: accepting a CALL knock
 * lets the caller dial in, accepting a CHAT knock opens the thread.
 */
export function KnockPrompts() {
  const { knocks, answerKnock } = useRealtime();
  const navigate = useNavigate();

  if (knocks.length === 0) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 z-40 w-[min(24rem,calc(100vw-2rem))] -translate-x-1/2 space-y-2 sm:left-4 sm:translate-x-0"
      role="region"
      aria-label="Interruption requests"
      aria-live="assertive"
    >
      {knocks.map((knock) => (
        <div
          key={knock.id}
          className="glass-strong animate-slide-up rounded-3xl p-4 ring-1 ring-inset ring-violet-400/50"
        >
          <div className="flex items-start gap-3">
            <span className="relative">
              <Avatar name={knock.fromUserName} photoUrl={knock.fromUserPhotoUrl} seed={knock.fromUserId} size="md" />
              <span
                className="absolute inset-0 animate-pulse-ring rounded-full border-2 border-violet-400"
                aria-hidden
              />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-slate-900">
                <Hand className="mr-1 inline h-3.5 w-3.5 text-violet-500" aria-hidden />
                {knock.fromUserName} wants to {knock.intent === 'CALL' ? 'call you' : 'talk'}
              </p>
              {knock.reason ? (
                <p className="mt-0.5 text-sm text-slate-600">“{knock.reason}”</p>
              ) : (
                <p className="mt-0.5 text-sm text-slate-500">
                  You are in focus mode, so nothing opens until you accept.
                </p>
              )}
              <p className="mt-1 text-xs text-slate-400">{relativeTime(knock.createdAt)}</p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  className="btn-primary px-2.5 py-1.5 text-xs"
                  onClick={() => {
                    answerKnock(knock.id, true);
                    if (knock.intent === 'CHAT') navigate(`/chat?user=${knock.fromUserId}`);
                  }}
                >
                  Accept
                </button>
                <button
                  type="button"
                  className="btn-secondary px-2.5 py-1.5 text-xs"
                  onClick={() => answerKnock(knock.id, false)}
                >
                  Not now
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
